import { useCallback, useMemo, useRef, useState } from "react";

import { FieldState } from "./inlineTones";
import { useInlineEdit, useRegisterSection } from "./InlineEditContext";

export type FieldErrors<T> = Partial<Record<keyof T, string>>;

/**
 * Outcome of a section save: `true` when it went through, `false` when it
 * didn't (the section already told the user why), or a map of field errors
 * to show next to each field.
 */
export type SaveResult<T> = boolean | FieldErrors<T>;

interface UseInlineSectionOptions<T extends object> {
  /** Unique id used by the page-level "save all". */
  id: string;
  /** Saved values, as the section displays them. */
  base: T;
  /**
   * Persists the section. Receives the full draft and the keys that changed.
   * It should resolve once fresh data is loaded, so the "saved" values are
   * already the ones coming from the server when the section goes back to
   * view mode.
   */
  onSave: (draft: T, dirtyKeys: Array<keyof T>) => Promise<SaveResult<T>>;
  /** Field equality, to tell an edit apart from the saved value. */
  isEqual?: (key: keyof T, a: unknown, b: unknown) => boolean;
  /** Every field is an input (e.g. while creating the record). */
  forceEdit?: boolean;
  /** The section can't be edited at all. */
  readOnly?: boolean;
}

const defaultIsEqual = (_key: unknown, a: unknown, b: unknown) => {
  if (a === b) return true;
  if (typeof a === "object" || typeof b === "object") {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }
  return false;
};

/**
 * Draft/edit/save state of one inline-editable section.
 *
 * Only the edited keys are kept (`edits`), layered over `base`, so a refetch
 * triggered by another section never wipes this section's pending changes.
 */
export function useInlineSection<T extends object>({
  id,
  base,
  onSave,
  isEqual = defaultIsEqual,
  forceEdit = false,
  readOnly = false,
}: UseInlineSectionOptions<T>) {
  const { globalEdit } = useInlineEdit();
  const [edits, setEdits] = useState<Partial<T>>({});
  const [editingKeys, setEditingKeys] = useState<Set<keyof T>>(new Set());
  const [savedKeys, setSavedKeys] = useState<Set<keyof T>>(new Set());
  const [errors, setErrors] = useState<FieldErrors<T>>({});
  const [isSaving, setIsSaving] = useState(false);

  const baseRef = useRef(base);
  baseRef.current = base;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const draft = useMemo(() => ({ ...base, ...edits }) as T, [base, edits]);

  const dirtyKeys = useMemo(
    () =>
      (Object.keys(edits) as Array<keyof T>).filter(
        (key) => !isEqual(key, edits[key], base[key])
      ),
    [edits, base, isEqual]
  );
  const dirtySet = useMemo(() => new Set(dirtyKeys), [dirtyKeys]);

  const clearError = useCallback(
    (keys: Array<keyof T>) =>
      setErrors((prev) => {
        if (!keys.some((key) => prev[key])) return prev;
        const next = { ...prev };
        keys.forEach((key) => delete next[key]);
        return next;
      }),
    []
  );

  /** Sets several fields at once; values equal to the saved one drop the edit. */
  const setFields = useCallback(
    (values: Partial<T>) => {
      const keys = Object.keys(values) as Array<keyof T>;
      setEdits((prev) => {
        const next = { ...prev };
        keys.forEach((key) => {
          if (isEqualRef.current(key, values[key], baseRef.current[key])) {
            delete next[key];
          } else {
            next[key] = values[key];
          }
        });
        return next;
      });
      setSavedKeys((prev) => {
        if (!keys.some((key) => prev.has(key))) return prev;
        const next = new Set(prev);
        keys.forEach((key) => next.delete(key));
        return next;
      });
      clearError(keys);
    },
    [clearError]
  );

  const setField = useCallback(
    <K extends keyof T>(key: K, value: T[K]) =>
      setFields({ [key]: value } as unknown as Partial<T>),
    [setFields]
  );

  const startEdit = useCallback((key: keyof T) => {
    setEditingKeys((prev) => new Set(prev).add(key));
  }, []);

  const openFields = useCallback((keys: Array<keyof T>) => {
    setEditingKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => next.add(key));
      return next;
    });
  }, []);

  /** Reverts one field (and any extra keys tied to it) and closes its input. */
  const cancelField = useCallback(
    (key: keyof T, linked: Array<keyof T> = []) => {
      const keys = [key, ...linked];
      setEdits((prev) => {
        const next = { ...prev };
        keys.forEach((k) => delete next[k]);
        return next;
      });
      setEditingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      clearError(keys);
    },
    [clearError]
  );

  const discard = useCallback(() => {
    setEdits({});
    setEditingKeys(new Set());
    setErrors({});
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (dirtyKeys.length === 0 && !forceEdit) {
      setEditingKeys(new Set());
      return true;
    }
    setIsSaving(true);
    try {
      const result = await onSave(draft, dirtyKeys);
      if (result === true) {
        setSavedKeys((prev) => {
          const next = new Set(prev);
          dirtyKeys.forEach((key) => next.add(key));
          return next;
        });
        setEdits({});
        setEditingKeys(new Set());
        setErrors({});
        return true;
      }
      if (result && typeof result === "object") {
        setErrors(result);
        // Open the fields with errors so the user can fix them right there.
        openFields(Object.keys(result) as Array<keyof T>);
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [dirtyKeys, draft, forceEdit, onSave, openFields]);

  useRegisterSection(id, readOnly ? 0 : dirtyKeys.length, { save, discard });

  const isDirty = useCallback((key: keyof T) => dirtySet.has(key), [dirtySet]);

  const fieldState = useCallback(
    (key: keyof T): FieldState => {
      if (errors[key]) return "error";
      if (dirtySet.has(key)) return "dirty";
      if (savedKeys.has(key)) return "saved";
      return "idle";
    },
    [errors, dirtySet, savedKeys]
  );

  const isEditing = useCallback(
    (key: keyof T) =>
      !readOnly && (forceEdit || globalEdit || editingKeys.has(key)),
    [readOnly, forceEdit, globalEdit, editingKeys]
  );

  /** Opened on its own (pencil), as opposed to via "edit all". */
  const isSoloEditing = useCallback(
    (key: keyof T) =>
      !readOnly && !forceEdit && !globalEdit && editingKeys.has(key),
    [readOnly, forceEdit, globalEdit, editingKeys]
  );

  return {
    draft,
    edits,
    errors,
    dirtyKeys,
    dirtyCount: dirtyKeys.length,
    isSaving,
    setField,
    setFields,
    startEdit,
    cancelField,
    discard,
    save,
    isDirty,
    fieldState,
    isEditing,
    isSoloEditing,
    hasOpenFields: editingKeys.size > 0,
  };
}

interface BindableSection<T> {
  fieldState: (key: keyof T) => FieldState;
  isEditing: (key: keyof T) => boolean;
  isSoloEditing: (key: keyof T) => boolean;
  isSaving: boolean;
  errors: FieldErrors<T>;
  startEdit: (key: keyof T) => void;
  save: () => Promise<boolean>;
  cancelField: (key: keyof T, linked?: Array<keyof T>) => void;
}

/**
 * Wires an InlineField to one key of a section.
 * `revert` replaces the default "drop this field's edit" (for fields whose
 * change also touched other fields).
 */
export function bindField<T>(
  section: BindableSection<T>,
  key: keyof T,
  editable: boolean,
  revert?: () => void
) {
  const undo = revert ?? (() => section.cancelField(key));
  return {
    state: section.fieldState(key),
    editable,
    editing: editable && section.isEditing(key),
    solo: section.isSoloEditing(key),
    saving: section.isSaving,
    error: section.errors[key],
    onEdit: () => section.startEdit(key),
    onSave: () => {
      void section.save();
    },
    onCancel: undo,
    onRevert: undo,
  };
}
